import { useEffect, useRef, useState } from "react";

/**
 * Scroll-reveal helper.
 *
 * Latches `hasEntered` the first time the element is meaningfully on screen so
 * a reveal plays once, and keeps reporting `isVisible` so callers can idle
 * looping animations while the element is scrolled away.
 *
 * Falls back to the finished state where IntersectionObserver is unavailable.
 */
export default function useInView({ threshold = 0.35 } = {}) {
  const ref = useRef(null);
  const [hasEntered, setHasEntered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setHasEntered(true);
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.intersectionRatio >= threshold) setHasEntered(true);
      },
      { threshold: [0, threshold] }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, hasEntered, isVisible];
}
